import { useState, useMemo, useCallback } from 'react';

export interface ConditionResourceFilters {
  search: string;
  resourceType: string;
  type: string;
  validationStatus: string;
  status: string;
  page: number;
  limit: number;
}

export const useConditionResourceFilters = (defaultResourceType?: string) => {
  const [filters, setFilters] = useState<ConditionResourceFilters>({
    search: '',
    resourceType: defaultResourceType || 'all',
    type: 'all',
    validationStatus: 'all',
    status: 'all',
    page: 1,
    limit: 25,
  });

  const handleFilterChange = useCallback(
    (key: keyof ConditionResourceFilters, value: string | number) => {
      setFilters((prev) => ({
        ...prev,
        [key]: value,
        // Reset page when filters change (except when changing page itself)
        ...(key !== 'page' && { page: 1 }),
      }));
    },
    []
  );

  const resetFilters = useCallback(() => {
    setFilters({
      search: '',
      resourceType: defaultResourceType || 'all',
      type: 'all',
      validationStatus: 'all',
      status: 'all',
      page: 1,
      limit: 25,
    });
  }, [defaultResourceType]);

  const queryParams = useMemo(() => {
    const params: Record<string, any> = {
      page: filters.page,
      per_page: filters.limit,
    };
    
    if (filters.search?.trim()) {
      params.text = filters.search.trim();
    }
    
    if (filters.resourceType && filters.resourceType !== 'all') {
      params.resource_type = filters.resourceType;
    }
    
    if (filters.type && filters.type !== 'all') {
      params.type = filters.type;
    }
    
    if (filters.validationStatus && filters.validationStatus !== 'all') {
      if (filters.validationStatus === 'valid') {
        params.is_valid = '1';
      } else if (filters.validationStatus === 'invalid') {
        params.is_valid = '0';
      } else if (filters.validationStatus === 'unknown') {
        params.is_valid = null;
      }
    }
    
    if (filters.status && filters.status !== 'all') {
      params.status = filters.status;
    }
    
    return params;
  }, [filters]);

  return {
    filters,
    queryParams,
    handleFilterChange,
    resetFilters,
  };
}; 